/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    STABLE_LOGIN_STATS_LOAD
 * Date:         2026-01-08
 * Modified By:  Cursor
 *
 * Description:
 * Memoize the load helper, align the effect dependency array, and remove unused React import.
 *
 * File Size Check:
 * - Current: 166
 * - Adding: ~25
 * - Projected: 191
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: n/a
 * - Tokens used: n/a
 *
 * Related Files:
 * - client/src/pages/admin/LoginsPage.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_LOGINS_PAGE
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Admin page to display login activity snapshot and recent failed attempts.
 */

// [2026-02-07] style-extraction: moved inline styles to SCSS module
import { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Spinner, Table, Button, Alert } from "react-bootstrap";
import { getLoginStats, LoginStatsResponse, RecentUser, RecentFailed } from "../../services/admin/loginStats";
import { formatDateTime } from "../../utils/dateFormat";
import "./LoginsPage.scss";
import styles from "./Admin.module.scss";

export default function LoginsPage() {
  const [data, setData] = useState<LoginStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [days] = useState<number>(7);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLoginStats(days);
      setData(res);
    } catch (err: any) {
      console.error("Failed to load login stats:", err);
      setError(err?.message || "Failed to load login stats");
    } finally {
      setLoading(false);
    }
  }, [days]);
  // [2026-01-08] load memoization: keep the helper stable for effect dependency tracking.

  useEffect(() => {
    load();
    // [2026-01-08] useEffect deps: rely on memoized load so lint accepts a stable dependency.
    // no interval by default; admin can refresh manually
  }, [load]);

  if (loading) {
    return (
      <div className={`d-flex justify-content-center align-items-center ${styles.centeredLoader}`}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">Failed to load login stats: {error}</Alert>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="admin-logins-page p-3">
      <Row className="mb-3 align-items-stretch">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Active Users (last {days} days)</Card.Title>
              <h3 className="mt-2">{data?.activeUsersCount ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Total loginCount</Card.Title>
              <h3 className="mt-2">{data?.totalLoginCount ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Failed attempts (24h)</Card.Title>
              <h3 className="mt-2">{data?.failedAttemptsCount24h ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Failed attempts (last {days} days)</Card.Title>
              <h3 className="mt-2">{data?.failedAttemptsCountWindow ?? 0}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Top recent users</strong>
              <div>
                <Button variant="outline-secondary" size="sm" onClick={load}>Refresh</Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Email / Name</th>
                      <th>Last Login</th>
                      <th className="text-end">loginCount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.recentUsers ?? []).map((u: RecentUser) => (
                      <tr key={u._id || `${u.email}-${u.lastLogin}`}>
                        <td>
                          <div>{u.email || `${u.firstName || ''} ${u.lastName || ''}`.trim()}</div>
                        </td>
                        <td>{formatDateTime(u.lastLogin)}</td>
                        <td className="text-end">{u.loginCount ?? 0}</td>
                      </tr>
                    ))}
                    {data && data.recentUsers.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center p-3">No recent users</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card>
            <Card.Header>
              <strong>Recent failed attempts (last 50)</strong>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table striped hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Email</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.recentFailedAttempts ?? []).map((f: RecentFailed, idx: number) => (
                      <tr key={f.userId ? `${f.userId}-${idx}` : `failed-${idx}`}>
                        <td>{f.userId ?? '-'}</td>
                        <td>{f.email ?? '(unknown)'}</td>
                        <td>{formatDateTime(f.createdAt)}</td>
                      </tr>
                    ))}
                    {data && data.recentFailedAttempts.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center p-3">No failed attempts</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
